import type { Job } from "@malfak/contracts";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Check, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Num, Skeleton } from "@/components/ui/misc";
import { fmtNumber, fmtPercent, fmtRelative } from "@/lib/format";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { BackfillDetails } from "./backfill-details";
import { JobControls } from "./job-controls";
import { JobError } from "./job-error";
import { BACKFILL_KIND, CATEGORY_ICON, countsText, etaText, isActive, jobLabel, STATUS_STROKE, STATUS_TONE, linkTo, targetHref } from "./model";
import { ProgressBar, ProgressRing } from "./progress-ring";
import { useJob } from "./use-jobs";

/**
 * Live progress of one job (ALP-67): ring + bar, status, counts, ETA and the controls the job allows.
 * Self-contained (fetches the job, listens to SSE), so any screen — or the Copilot's `job` block — can embed it.
 */
export function JobProgress({ jobId, compact = false }: { jobId: string; compact?: boolean }) {
  const { data: job, isLoading } = useJob(jobId);
  if (!job) {
    return isLoading ? <Skeleton className={compact ? "h-12 rounded-xl" : "h-28 rounded-2xl"} /> : null;
  }
  return compact ? <CompactJob job={job} /> : <FullJob job={job} />;
}

function StatusIcon({ job }: { job: Job }) {
  const Icon = CATEGORY_ICON[job.category];
  if (job.status === "succeeded") return <Check className="size-3.5 text-ok" strokeWidth={3} />;
  if (job.status === "failed") return <X className="size-3.5 text-bad" strokeWidth={3} />;
  return <Icon className="size-3.5 text-ink-2" />;
}

/** Accessible progress semantics around the decorative ring/bar. */
function progressA11y(job: Job, label: string, t: ReturnType<typeof useT>["t"]) {
  return {
    role: "progressbar" as const,
    "aria-label": t("jobs.progressLabel", { title: label }),
    "aria-valuemin": 0,
    "aria-valuemax": 100,
    ...(job.progress !== null ? { "aria-valuenow": Math.round(job.progress * 100) } : {}),
    "aria-valuetext": `${t(`jobs.status.${job.status}`)} · ${countsText(job, t)}`,
  };
}

function CompactJob({ job }: { job: Job }) {
  const { t } = useT();
  const label = jobLabel(job, t);
  const eta = etaText(job, t);
  return (
    <div className="flex items-center gap-2.5 rounded-xl px-2 py-2" data-testid="job-progress" data-job-id={job.id} data-status={job.status}>
      <span {...progressA11y(job, label, t)} className="shrink-0">
        <ProgressRing value={job.status === "running" || job.status === "queued" ? job.progress : (job.progress ?? 1)} size={30} stroke={3} color={STATUS_STROKE[job.status]} glow={job.status === "running"}>
          <StatusIcon job={job} />
        </ProgressRing>
      </span>
      <div className="min-w-0 flex-1 leading-tight">
        <div className="truncate text-[12.5px] font-medium text-ink" title={label}>
          {label}
        </div>
        <div className="mt-0.5 flex items-center gap-1.5 truncate text-[11px] text-ink-3">
          <span className={cn("font-medium", job.status === "failed" && "text-bad", job.status === "paused" && "text-warn")}>{t(`jobs.status.${job.status}`)}</span>
          <span aria-hidden>·</span>
          {job.kind === BACKFILL_KIND && job.result ? <BackfillDetails job={job} compact /> : <Num>{countsText(job, t)}</Num>}
          {eta && isActive(job.status) && (
            <>
              <span aria-hidden>·</span>
              <span className="truncate">{eta}</span>
            </>
          )}
        </div>
      </div>
      <JobControls job={job} compact />
    </div>
  );
}

function FullJob({ job }: { job: Job }) {
  const { t } = useT();
  const reduce = useReducedMotion();
  const label = jobLabel(job, t);
  const eta = etaText(job, t);
  const href = targetHref(job, useQueryClient());
  const pct = job.progress === null ? null : fmtPercent(job.progress);
  return (
    <div className="rounded-2xl border border-line bg-surface p-4" data-testid="job-progress" data-job-id={job.id} data-status={job.status}>
      <div className="flex items-start gap-3">
        <span {...progressA11y(job, label, t)}>
          <ProgressRing value={isActive(job.status) ? job.progress : (job.progress ?? 1)} size={48} stroke={4} color={STATUS_STROKE[job.status]} glow={job.status === "running"}>
            {pct && isActive(job.status) ? <span className="num text-[10.5px] font-semibold text-ink">{pct}</span> : <StatusIcon job={job} />}
          </ProgressRing>
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {href ? (
              <Link {...linkTo(href)} data-testid="job-target-link" className="truncate text-[14px] font-semibold text-ink hover:underline">
                {label}
              </Link>
            ) : (
              <span className="truncate text-[14px] font-semibold text-ink">{label}</span>
            )}
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.span key={job.status} initial={reduce ? false : { opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} exit={reduce ? undefined : { opacity: 0, scale: 0.85 }}>
                <Badge tone={STATUS_TONE[job.status]} size="xs" data-testid="job-status">
                  {t(`jobs.status.${job.status}`)}
                </Badge>
              </motion.span>
            </AnimatePresence>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-ink-3">
            <Num>{countsText(job, t)}</Num>
            {job.failed > 0 && <span className="font-medium text-bad">{t("jobs.counts.failed", { count: fmtNumber(job.failed) })}</span>}
            {job.skipped > 0 && <span>{t("jobs.counts.skipped", { count: fmtNumber(job.skipped) })}</span>}
            {eta && <span className={cn(job.status === "paused" && "text-warn")}>{eta}</span>}
            {!isActive(job.status) && job.finishedAt && <span>{fmtRelative(job.finishedAt)}</span>}
          </div>
        </div>
      </div>
      <BackfillDetails job={job} className="mt-2" />
      <ProgressBar className="mt-3" value={isActive(job.status) ? job.progress : (job.progress ?? 1)} color={STATUS_STROKE[job.status]} running={job.status === "running"} />
      <JobError job={job} />
      <JobControls job={job} className="mt-3" />
    </div>
  );
}
